import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { Platform } from 'react-native';

import { getColors, type ThemeColors } from '@/styles/theme';

// Define our custom theme interface
export interface AppTheme {
	colors: {
		primary: string;
		primaryContainer: string;
		secondary: string;
		secondaryContainer: string;
		success: string;
		error: string;
		errorLight: string;
		warning: string;
		surface: string;
		background: string;
		outline: string;
		onPrimary: string;
		onSecondary: string;
		onError: string;
		onSurface: string;
		onBackground: string;
		// Additional colors for compatibility
		surfaceVariant: string;
		onSurfaceVariant: string;
		onPrimaryContainer: string;
		tertiary: string;
		tertiaryContainer: string;
		onTertiary: string;
		onTertiaryContainer: string;
		surfaceDisabled: string;
		onSurfaceDisabled: string;
		outlineVariant: string;
		shadow: string;
		scrim: string;
		inverseSurface: string;
		onInverseSurface: string;
		inversePrimary: string;
		errorContainer: string;
		onErrorContainer: string;
	};
	roundness: number;
	version: number;
	isDark: boolean;
}

// Function to create theme object based on colors
function createTheme(colors: ThemeColors, isDark: boolean): AppTheme {
	return {
		colors: {
			primary: colors.primary,
			primaryContainer: colors.primaryLight,
			secondary: colors.secondary,
			secondaryContainer: colors.secondaryLight,
			success: colors.success,
			error: colors.error,
			errorLight: colors.errorLight,
			warning: colors.link,
			surface: colors.card,
			background: colors.background,
			outline: colors.border,
			onPrimary: isDark ? '#000000' : '#ffffff',
			onSecondary: isDark ? '#000000' : '#ffffff',
			onError: '#ffffff',
			onSurface: colors.text,
			onBackground: colors.text,
			// Additional colors for compatibility
			surfaceVariant: isDark ? '#2c2c2c' : '#e5e5e5',
			onSurfaceVariant: colors.textSecondary,
			onPrimaryContainer: isDark ? '#000000' : '#ffffff',
			tertiary: colors.purple,
			tertiaryContainer: colors.purpleLight,
			onTertiary: '#ffffff',
			onTertiaryContainer: isDark ? '#4a148c' : '#ffffff',
			surfaceDisabled: isDark ? '#1a1a1a' : '#f0f0f0',
			onSurfaceDisabled: colors.textLight,
			outlineVariant: isDark ? '#404040' : '#e0e0e0',
			shadow: colors.shadow,
			scrim: colors.scrim,
			inverseSurface: isDark ? '#ffffff' : '#2c2c2c',
			onInverseSurface: isDark ? '#000000' : '#ffffff',
			inversePrimary: isDark ? colors.primary : colors.primaryLight,
			errorContainer: colors.errorLight,
			onErrorContainer: colors.errorDark,
		},
		roundness: 4,
		version: 3,
		isDark,
	};
}

// Create theme context
const ThemeContext = createContext<AppTheme | null>(null);

// Create context for theme toggle
export interface ThemeContextType {
	theme: AppTheme;
	isDark: boolean;
	toggleTheme: () => void;
}

const ThemeToggleContext = createContext<ThemeContextType | null>(null);

// Theme provider component - dark mode disabled, always light
export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const isDark = false;

	useEffect(() => {
		if (Platform.OS === 'web') {
			document.documentElement.setAttribute('data-theme', 'light');
		}
	}, []);

	const colors = getColors(isDark);
	const theme = createTheme(colors, isDark);

	const toggleTheme = () => {};

	return (
		<ThemeContext.Provider value={theme}>
			<ThemeToggleContext.Provider value={{ theme, isDark, toggleTheme }}>
				{children}
			</ThemeToggleContext.Provider>
		</ThemeContext.Provider>
	);
};

// Custom hook to use theme
export const useTheme = (): AppTheme => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};

// Custom hook to use theme toggle functionality
export const useThemeToggle = (): ThemeContextType => {
	const context = useContext(ThemeToggleContext);
	if (!context) {
		throw new Error('useThemeToggle must be used within a ThemeProvider');
	}
	return context;
};

export default ThemeProvider;
